import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Icon } from '@components';
import { FEATURE_FLAGS } from '@constants/feature';
import { GROUP_MEMBER_MAX_COUNT } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import { useGroupFileUpload } from '@features/group-management/hooks/useGroupFileUpload';
import { ImportFileTabProps } from '@types';
import { getLocalStorageItem } from '@utils/storage';

import DownloadAndViewTemplate from './DownloadAndViewTemplate';
import DragAndDropFileSection from './DragAndDropFileSection';
import GroupFileUploadResultModal from './GroupFileUploadResultModal';

import theme from '../../../../../../../themes/colors';

const ImportFileTab = ({
  handleAdd,
  memberFile,
  setMemberFile,
  groupFeeConfigurations,
  isOnce,
  groupFrequency,
  groupIntervalFrequency,
  isCustomTerm,
  termsData,
  groupStartDate,
  groupEndDate,
  groupAmount,
  isImmediatePayment,
  isFeeCategoryEnabled,
  isAttendanceBasedFee,
  isSessionBased,
  sessionsPerCycle,
  activeGroupId,
  defaultTermLabel
}: ImportFileTabProps) => {
  const { groupId } = useParams();
  const { orgFeatureFlags } = useSelector((state: any) => state.user);

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);

  const isLeadSquared = orgFeatureFlags?.includes(FEATURE_FLAGS.LEAD_SQUARED);
  const isEndDateEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.MEMBER_END_DATE
  );

  const feeCategories =
    groupFeeConfigurations?.map((value) => value.category) || [];
  const termPrefix = defaultTermLabel || 'Installment';
  const termsName =
    termsData?.map(
      (item) => item.label || `${termPrefix} ${item.term_number}`
    ) || [];

  const {
    // Step state
    step,

    // File state
    file,
    fileName,
    fileUploadError,
    isLoading,
    showPrimaryNumberWarning,
    headers,
    displayHeaders,
    rows,

    // Response data for ParsedDataGrid
    displayRows,
    rowErrorsById,

    // Computed counts
    totalMembers,
    membersWithErrors,
    membersWithoutErrors,
    warningCount,

    // UI labels
    primaryButtonLabel,
    isPrimaryButtonDisabled,

    // Loading state
    isApiLoading,

    // Handlers
    onFileSelect,
    checkFileValidityAndProcess,
    deleteFile,
    handleCancel,
    handleNextClick,
    handleProceedClick
  } = useGroupFileUpload({
    organizationId,
    branchId,
    groupId: groupId || activeGroupId || '',
    groupFeeConfigurations,
    isOnce,
    groupFrequency,
    groupIntervalFrequency,
    isCustomTerm,
    termsData,
    groupStartDate,
    groupEndDate,
    isImmediatePayment,
    groupAmount,
    isFeeCategoryEnabled,
    isAttendanceBasedFee,
    isSessionBased,
    groupSessionsPerCycle: sessionsPerCycle,
    defaultTermLabel,
    onProceed: (members) => {
      handleAdd(members);
      setMemberFile?.(undefined);
    }
  });

  // Handle memberFile changes from parent
  useEffect(() => {
    if (memberFile && !file) {
      checkFileValidityAndProcess(memberFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberFile, groupFrequency, groupIntervalFrequency]);

  // Check if modal should be open (when in error_preview or warning_preview step)
  const isModalOpen = step === 'error_preview' || step === 'warning_preview';

  return (
    <div className="min-h-120 rounded-lg bg-theme p-6">
      {/* Always show the upload UI */}
      <DownloadAndViewTemplate
        tableHeaders={headers}
        tableRows={rows}
        isFileAvailable={!!file}
        feeCategories={feeCategories}
        isOnce={isOnce}
        isCustomTerm={isCustomTerm || false}
        termsName={termsName}
        isLeadSquared={isLeadSquared}
        isEndDateEnabled={isEndDateEnabled}
        isAttendanceBasedFee={isAttendanceBasedFee}
        isSessionBased={isSessionBased}
      />
      <DragAndDropFileSection
        file={file}
        isLoading={isLoading}
        onFileSelect={onFileSelect}
        checkFileValidityAndProcess={checkFileValidityAndProcess}
        fileUploadError={fileUploadError}
        onDeleteFile={() => {
          deleteFile();
          setMemberFile?.(undefined);
          handleAdd([]);
        }}
        description={`Maximum no. of members allowed in a single file upload is ${GROUP_MEMBER_MAX_COUNT}`}
      />
      {showPrimaryNumberWarning && file && (
        <div className="mt-6 flex flex-col gap-1 rounded-lg bg-yellow10 p-4">
          <div className="flex items-center gap-2">
            <Icon
              name="alert-circle"
              stroke={theme.yellow40}
              data-tooltip-id="kyc-pending-info-icon"
              className="!size-4"
            />
            <div className="text-sm font-medium text-yellow40">
              Warning: One or more mobile numbers in your list end with 00000.
              This issue may be caused by Excel auto-formatting. Please review
              and correct the numbers if needed.
            </div>
          </div>
        </div>
      )}

      {/* Modal for error/warning preview */}
      <GroupFileUploadResultModal
        isOpen={isModalOpen}
        step={step}
        fileName={fileName}
        headers={displayHeaders}
        displayRows={displayRows}
        rowErrorsById={rowErrorsById}
        totalMembers={totalMembers}
        membersWithErrors={membersWithErrors}
        membersWithoutErrors={membersWithoutErrors}
        warningCount={warningCount}
        primaryButtonLabel={primaryButtonLabel}
        isPrimaryButtonDisabled={isPrimaryButtonDisabled}
        isApiLoading={isApiLoading}
        onClose={handleCancel}
        onNextClick={handleNextClick}
        onProceedClick={handleProceedClick}
      />
    </div>
  );
};

export default ImportFileTab;
